import { useParams } from 'react-router-dom';
import { useGetFamilyByIdQuery, useRemoveFamilyMutation, useUpdateFamilyMutation } from '../api/familyAPI';
import { useGetUserByIdQuery } from '../api/userAPI';
import Button from '../components/UI/Button';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../store/slices/authSlice';
import { updateFamilyInput } from '../features/families/familySchema';
import UpdateFamilyForm from '../components/UpdateFamilyProfile';
import { Paragraph } from '../components/UI/Typography';

export default function FamilyProfile() {
  const { id } = useParams<{ id: string }>();
  const { data: family, isLoading, error } = useGetFamilyByIdQuery(id!);
  const { data: mentor } = useGetUserByIdQuery(family?.mentor_id || "9ad54846-a3c6-4e87-9b0b-ad2b1d361841");
  const user = useSelector(selectCurrentUser);
  
  const [updateFamily] = useUpdateFamilyMutation();
  const [removeFamily, { isLoading: deleting }] = useRemoveFamilyMutation();

  const [message, setMessage] = useState<{ success: boolean; text: string } | null>(null);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleClickUpdate = () => {
    setMessage(null);
    setIsUpdating(true);
  }

  const handleSubmitUpdate = async (data: updateFamilyInput) => {
    try {
      await updateFamily({ id: id!, familyData: data }).unwrap();
      setMessage({ success: true, text: 'פרופיל משפחה עודכן בהצלחה' });
      setIsUpdating(false);
    } catch (err: any) {
      setActionsError(err.message || 'שגיעה במהלך עדכון פרופיל משפחה');
    }
  }

  const handleDelete = async () => {
    if (family?.id && window.confirm("אתה בטוח שברצונך להסיר משפחה זו?")) {
      try {
        await removeFamily(family.id).unwrap();
        setMessage({ success: true, text: "המשפחה הוסרה בהצלחה" });
      } catch (err: any) {
        setActionsError("נכשלה הסרת המשפחה");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full py-10">
        <p className="text-blue-600 text-lg animate-pulse">טוען נתונים...</p>
      </div>
    );
  }

  if (error || !family) {
    return (
      <div className="flex justify-center items-center h-full py-10">
        <p className="text-red-600 text-lg">שגיאה בטעינת פרטי המשפחה</p>
      </div>
    );
  }

  const mentorName = mentor?.name || "";

  return (
    <>
      {!isUpdating ? (
        <div className="max-w-xl mx-auto p-4 sm:p-6 md:p-8 bg-white shadow-lg rounded-lg mt-6">
          <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-4">
            משפחת {family.family}
          </h1>
          <div className="space-y-3 text-gray-700 text-base md:text-lg">
            <p><span className="font-semibold">טלפון:</span> {family.phone}</p>
            {family.email && (
              <p><span className="font-semibold">אימייל:</span> {family.email}</p>
            )}
            <p><span className="font-semibold">סטטוס:</span> {family.status}</p>
            <p><span className="font-semibold">מס' ילדים:</span> {family.num_of_children}</p>
            <p><span className="font-semibold">מלווה:</span> {mentorName}</p>
          </div>
          <div className="w-full flex justify-end gap-2 mt-10">
            <Button onClick={handleClickUpdate} variant="outline" type="button" className="w-full rounded-full py-3 text-lg font-bold">
              עריכת פרופיל
            </Button>
            {user && user.role === "admin" && (
              <Button onClick={handleDelete} variant="primary-dark" type="button" className="w-full rounded-full py-3 text-lg font-bold" isLoading={deleting}>
                ביטול
              </Button>
            )}
          </div>
          {message && (
            <Paragraph className="text-success text-center font-semibold">
              {message.text}
            </Paragraph>
          )}
          {actionsError && (
            <Paragraph className="text-danger text-center font-semibold">
              {actionsError}
            </Paragraph>
          )}
        </div>
      ) : (
        <div className="p-4 bg-white rounded shadow-md max-w-xl mx-auto">
          <UpdateFamilyForm
            onSubmit={handleSubmitUpdate}
            isLoading={false} 
            message={message?.text || ''}
            error={actionsError}
            defaultValues={{
              family: family.family,
              phone: family.phone || "",
              email: family.email,
              num_of_children: String(family.num_of_children),
              status: family.status,
              mentorName: mentorName,
            }}
          />
        </div>
      )}
    </>
  );
}
